import { Link } from "react-router-dom";
import { Database } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/context/AppContext";
import { StateBadge } from "@/components/StateBadge";
import { useApi } from "@/lib/useApi";

export const PageHead = ({ title, sub, children }) => (
  <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
    <div>
      <h1 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">{title}</h1>
      {sub && <p className="mt-2 text-sm text-slate-400">{sub}</p>}
    </div>
    <div className="flex flex-wrap gap-3">{children}</div>
  </div>
);

export const PrimaryBtn = ({ children, className = "", ...p }) => (
  <button {...p} className={`inline-flex items-center gap-2 rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-slate-950 transition-opacity duration-200 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40 ${className}`}>{children}</button>
);

export const GhostBtn = ({ children, className = "", ...p }) => (
  <button {...p} className={`inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm text-slate-200 transition-colors duration-200 hover:border-white/40 disabled:cursor-not-allowed disabled:opacity-40 ${className}`}>{children}</button>
);

export default function Dashboard() {
  const { t } = useLang();
  const { data: d, reload: load } = useApi("/dashboard");

  const seed = async () => {
    try { await api.post("/demo/seed"); toast.success(t("seed_ok")); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const stats = [["stat_principals", d?.principals, "/app/principals"], ["stat_intents", d?.intents, "/app/intents"], ["stat_rel", d?.relationships, "/app/connections"]];
  return (
    <div>
      <PageHead title={t("dash_title")} sub={t("dash_sub")}>
        <PrimaryBtn onClick={seed} data-testid="seed-demo-btn"><Database className="h-4 w-4" />{t("seed_demo")}</PrimaryBtn>
      </PageHead>
      <div className="grid gap-6 md:grid-cols-3">
        {stats.map(([k, v, to]) => (
          <Link to={to} key={k} className="rounded-md border border-white/[0.08] bg-[#111827] p-8 transition-colors duration-200 hover:border-emerald-400/30" data-testid={`stat-${k}`}>
            <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{t(k)}</p>
            <p className="mt-4 font-display text-5xl font-bold text-white">{v ?? "–"}</p>
          </Link>
        ))}
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="rounded-md border border-white/[0.08] bg-[#111827] p-8 lg:col-span-2">
          <p className="font-mono text-[11px] uppercase tracking-widest text-emerald-400">{t("flow_title")}</p>
          <ol className="mt-6 grid gap-4 sm:grid-cols-2">
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <li key={n} className="flex gap-4"><span className="font-display text-2xl font-bold text-emerald-400/40">{n}</span><span className="pt-1 text-sm text-slate-300">{t(`flow_${n}`)}</span></li>
            ))}
          </ol>
        </div>
        <div className="rounded-md border border-white/[0.08] bg-[#111827] p-8" data-testid="dashboard-states">
          <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{t("state")}</p>
          <div className="mt-6 space-y-3">
            {Object.entries(d?.states || {}).map(([s, n]) => (
              <div key={s} className="flex items-center justify-between"><StateBadge state={s} testId={`dash-state-${s}`} /><span className="font-mono text-sm text-white">{n}</span></div>
            ))}
            {d && !Object.keys(d.states).length && <p className="text-sm text-slate-500">{t("empty")}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
